/**
 * The pieces every marketing screen is built from: number formats, a card, a
 * labelled field, a stat, a chip and a proportional bar.
 *
 * They live here rather than in each panel because the audience numbers are the
 * whole point of the module - "4 000" and "4000" side by side on the same screen
 * is how somebody misreads a send size by an order of magnitude, and a chip that
 * looks like a toggle on one tab and like a tag on the next is how somebody
 * stops trusting either.
 *
 * Since the CRM redesign this file is a thin layer over `crm/ui` rather than a
 * second design of its own. That is the point of keeping it: nineteen files
 * import these six components, so moving the module onto the app's vocabulary
 * is one edit here instead of nineteen edits out there - and the module keeps
 * its own names for things, which is what stopped the campaign panels from
 * being ported piecemeal in the first place.
 */
import type { ReactNode } from "react";
import { cn } from "@wa/ui";
import type { CampaignStatus } from "../../../lib/marketing";
import { StatusPill as CrmStatusPill } from "../../ui";
import { campaignTone } from "./status";

/** Grouped thousands with a non-breaking space, so 361999 reads as 361 999
 *  and never wraps across a line break mid-number. */
export function num(n: number | null | undefined): string {
  if (n == null) return "-";
  return n.toLocaleString("et-EE").replace(/\s/g, "\u00a0");
}

export function dt(s: string | null | undefined): string {
  if (!s) return "-";
  return new Date(s).toLocaleString("et-EE", { dateStyle: "short", timeStyle: "short" });
}

/** A share as a rounded percentage. Never rounds a non-zero share down to "0%"
 *  or an incomplete one up to "100%": both would claim something the number does
 *  not say, and this module's job is to not do that. */
export function pct(part: number, whole: number): string {
  if (!whole) return "0%";
  const raw = (part / whole) * 100;
  if (raw > 0 && raw < 1) return "<1%";
  if (raw < 100 && raw > 99) return ">99%";
  return `${Math.round(raw)}%`;
}

/**
 * The campaign's standing.
 *
 * Was a hand-rolled colour map in emerald-500 and amber-500, which meant the
 * campaign list and the campaign editor drew the same five words in two
 * different palettes. Both now go through `campaignTone`, so `done` is the same
 * quiet grey in both places and `active` the same green.
 */
export function StatusPill({ status, label }: { status: CampaignStatus; label: string }) {
  return (
    <CrmStatusPill tone={campaignTone(status)} className={status === "active" ? "crm-st-live" : undefined}>
      {label}
    </CrmStatusPill>
  );
}

/** A labelled control. `hint` is for the sentences this module cannot do
 *  without - most of its filters mean something narrower than their name.
 *  `badge` sits on the label line, for facts about the field itself (how much
 *  of the register it can actually see). */
export function Field({
  label,
  hint,
  badge,
  className,
  children,
}: {
  label: string;
  hint?: ReactNode;
  badge?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    // `min-w-0` on the label itself, not only on whatever is inside it. A grid
    // track sizes to its content and the label IS the content, which is how a
    // number input in the register's filters once demanded 484px of track.
    <label className={cn("block min-w-0", className)}>
      <span className="crm-lab !mt-0 flex items-center gap-2">
        {label}
        {badge}
      </span>
      {children}
      {hint ? <span className="crm-sub mt-1.5 block text-[11px] leading-snug">{hint}</span> : null}
    </label>
  );
}

/**
 * A card section. The chrome is the CRM's card, so a campaign tab reads as the
 * same piece of software as the client list rather than as a screen bolted on
 * from somewhere else.
 */
export function Panel({
  title,
  description,
  icon,
  actions,
  className,
  children,
}: {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn("crm-card", className)}>
      <div className="crm-card-head">
        {icon ? <span className="crm-kpi-tile crm-panel-mark">{icon}</span> : null}
        <div className="crm-card-col">
          <div className="crm-card-t">{title}</div>
          {description ? <div className="crm-card-s">{description}</div> : null}
        </div>
        {actions ? <div className="crm-card-act">{actions}</div> : null}
      </div>
      {/* The card has no padding of its own - see `crm-card` - so the body
          brings its own, matching the head's 26px gutter. */}
      <div className="px-[26px] pb-6 pt-4">{children}</div>
    </section>
  );
}

export type Tone = "good" | "warn" | "bad" | "muted" | "neutral";

/** Colour expressions rather than class names, the same way `Kpi` does it: one
 *  value paints the figure and the bar, and nothing here depends on a content
 *  scan having seen the class written out. */
const TONE_TEXT: Record<Tone, string | undefined> = {
  good: "var(--color-success)",
  warn: "color-mix(in oklab, var(--color-warning) 55%, var(--color-foreground))",
  bad: "var(--color-destructive)",
  muted: "var(--color-muted-foreground)",
  neutral: undefined,
};

const TONE_FILL: Record<Tone, string> = {
  good: "var(--color-success)",
  warn: "var(--color-warning)",
  bad: "var(--color-destructive)",
  muted: "color-mix(in oklab, var(--color-foreground) 30%, transparent)",
  neutral: "linear-gradient(90deg, var(--color-primary), var(--color-primary-2))",
};

/** One number with a caption, optionally emphasised as the panel's headline.
 *  `sub` is the second line the number needs to be honest - a share, a unit. */
export function Stat({
  label,
  value,
  sub,
  tone = "neutral",
  size = "md",
  title,
  className,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  size?: "md" | "lg";
  title?: string;
  className?: string;
}) {
  return (
    <div className={cn("crm-pstat", className)} title={title}>
      <b className={cn(size === "lg" && "text-[32px]")} style={{ color: TONE_TEXT[tone] }}>
        {value}
      </b>
      <span>{label}</span>
      {sub ? <span className="crm-pstat-sub">{sub}</span> : null}
    </div>
  );
}

/** A share of a whole, drawn. Used wherever this module claims a proportion -
 *  a drop reason against everything dropped, a funnel step against the step
 *  above it. Drawn rather than only written because "1 767 of 19 800" is a
 *  sentence you have to do arithmetic on. */
export function Bar({ value, total, tone = "neutral", className }: {
  value: number;
  total: number;
  tone?: Tone;
  className?: string;
}) {
  // A share that rounds to nothing still gets a visible sliver: a bar that
  // disappears reads as zero, and zero is a different claim.
  const share = total > 0 ? Math.max(value > 0 ? 1.5 : 0, (value / total) * 100) : 0;
  return (
    <div className={cn("crm-meter crm-meter-sm", className)}>
      <i style={{ width: `${Math.min(100, share)}%`, background: TONE_FILL[tone] }} />
    </div>
  );
}

/** A toggleable pill. Three screens in this module had grown their own copy of
 *  this with slightly different borders; they now share the app's. */
export function Chip({
  active,
  onClick,
  disabled,
  count,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  count?: ReactNode;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn("crm-cb crm-cb-wide crm-cb-sm", active && "is-on")}
    >
      {children}
      {count != null ? <span className="crm-cb-n">{count}</span> : null}
    </button>
  );
}

const NOTE_TONE: Record<"warn" | "bad" | "info", "wt" | "lt" | "mut"> = {
  warn: "wt",
  bad: "lt",
  info: "mut",
};

/** A callout. Every warning in this module used to be its own hand-rolled
 *  paragraph, which is why some were amber and some were red for the same
 *  severity. The severity is now carried by the same status pill the rest of
 *  the CRM uses, so a warning here and a warning on the client list are
 *  recognisably the same claim. */
export function Note({
  tone,
  icon,
  children,
  className,
}: {
  tone: "warn" | "bad" | "info";
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("crm-card flex items-start gap-2.5 p-3.5", className)}>
      {icon ? <CrmStatusPill tone={NOTE_TONE[tone]} icon>{icon}</CrmStatusPill> : null}
      <div className={cn("crm-sub min-w-0 flex-1", tone === "bad" && "text-[var(--color-destructive)]")}>
        {children}
      </div>
    </div>
  );
}

export function CountBadge({ children }: { children: ReactNode }) {
  return <span className="crm-chip crm-tnum">{children}</span>;
}
